import { ApiError, ApiResponse } from '@/api/types'
import {
  GetMenuArgs,
  QrMenuCheckDetailsResponse,
  QrMenuCreateOrderPayload,
  QrMenuTableOrderPayload,
  QrMenuFastOrderResponse,
  QrMenuGetOrderByUuidResponse,
  QrMenuData,
  QrMenuOrderType,
  TapLinkStoreResponse
} from '@/composables/qr-menu/types'
import { DEFAULT_LANGUAGE } from '@/plugins/i18n/models'
import { AxiosError, isAxiosError } from 'axios'
import { useAppToast } from '@/utils/toast'
import { ref } from 'vue'
import $clientApi from '@/api/client'
import { RestaurantType } from '@/composables/restaurants/types'
import { RouteParamValue } from 'vue-router'

export const useQRMenuRequests = () => {
  const { showErrorToast } = useAppToast()
  const loading = ref(false)

  const getList = async ({
    brandId,
    storeId,
    tableId,
    delivery,
    fast,
    locale = DEFAULT_LANGUAGE
  }: GetMenuArgs) => {
    let type: RestaurantType = 'demo'
    let endpoint
    const params: Record<string, string> = {}

    if (tableId) {
      // Если есть tableId - только tableId
      params.type = 'on_table'
      params.table = tableId
      params.brand_id = brandId
      endpoint = '/../menu/list'
    } else if (delivery) {
      // Если delivery и storeId - только storeId и type=delivery
      type = 'delivery'
      params.type = type
      params.store_id = delivery
      endpoint = '/../menu/list'
    } else if (fast) {
      // Если delivery и storeId - только storeId и type=delivery
      type = 'fast'
      params.type = type
      params.store_id = fast
      endpoint = '/../menu/list'
    } else if (storeId) {
      // Если только storeId - только storeId и type=takeaway
      type = 'demo'
      params.type = type
      params.store_id = storeId
      endpoint = '/../menu/list'
    } else {
      // Если только brandId - только brandId и type=demo
      type = 'demo'
      params.type = type
      params.brand_id = brandId
      endpoint = '/../menu/list'
    }

    // qr-menu: использует VITE_TIPS_URL (с /tips)
    const baseUrl = (import.meta.env.VITE_TIPS_URL as string).replace(/\/tips\/?$/, '')
    try {
      const res = await $clientApi.get<ApiResponse<QrMenuData>>(endpoint, {
        baseURL: baseUrl,
        params,
        headers: {
          'x-locale': locale.split('-')[0]
        }
      })
      const body = res.data
      if (!body.success) {
        const fail = body as unknown as ApiError
        const msg =
          fail.error?.details || fail.error?.message || 'Не удалось загрузить меню'
        throw new Error(msg)
      }
      return body
    } catch (e) {
      if (e instanceof Error && !isAxiosError(e)) {
        throw e
      }
      const err = e as AxiosError<ApiError>
      const msg =
        err.response?.data?.error?.details ||
        err.response?.data?.error?.message ||
        err.message ||
        'Не удалось загрузить меню'
      throw new Error(msg)
    }
  }

  const createOrder = async (payload: QrMenuCreateOrderPayload): Promise<QrMenuFastOrderResponse> => {
    const res = await $clientApi.post<QrMenuFastOrderResponse>('/../menu/order/fast', payload)
    return res.data
  }

  const createTableOrder = async (payload: QrMenuTableOrderPayload): Promise<QrMenuFastOrderResponse> => {
    const res = await $clientApi.post<QrMenuFastOrderResponse>('/../menu/order/table', payload)
    return res.data
  }

  const confirmOrderPayment = async (paymentId: string, otp: number) => {
    const baseUrl = (import.meta.env.VITE_TIPS_URL as string).replace(/\/tips\/?$/, '')
    const res = await $clientApi.post<ApiResponse<unknown>>(
      `/menu/payment/${encodeURIComponent(paymentId)}`,
      { otp },
      { baseURL: baseUrl }
    )
    return res.data
  }

  const resendOrderPaymentOtp = async (paymentId: string, cardToken: string, captcha: string) => {
    const baseUrl = (import.meta.env.VITE_TIPS_URL as string).replace(/\/tips\/?$/, '')
    const res = await $clientApi.post<QrMenuFastOrderResponse>(
      `/menu/payment/${encodeURIComponent(paymentId)}/resend`,
      { card: { token: cardToken }, recaptcha: captcha },
      { baseURL: baseUrl }
    )
    return res.data
  }

  const getStore = async (id: string, locale = DEFAULT_LANGUAGE) => {
    const baseUrl = (import.meta.env.VITE_TIPS_URL as string).replace(/\/tips\/?$/, '')
    const res = await $clientApi.get<ApiResponse<TapLinkStoreResponse>>(`/menu/store/${id}`, {
      baseURL: baseUrl,
      headers: {
        'x-locale': locale.split('-')[0]
      }
    })

    return res.data
  }

  const getOrderByUuid = async (uuid: string, locale = DEFAULT_LANGUAGE) => {
    const baseUrl = (import.meta.env.VITE_TIPS_URL as string).replace(/\/tips\/?$/, '')
    const res = await $clientApi.get<QrMenuGetOrderByUuidResponse>(
      `/menu/order/${encodeURIComponent(uuid)}`,
      {
        baseURL: baseUrl,
        headers: {
          'x-locale': locale.split('-')[0]
        }
      }
    )
    return res.data
  }

  const getCreatedOrder = async (id: string | RouteParamValue[], type: QrMenuOrderType) => {
    loading.value = true
    try {
      const res = await $clientApi.get<QrMenuCheckDetailsResponse>(`/menu/qrmenu/view-order`, {
        params: { type, id }
      })
      return res.data
    } catch (error) {
      const err = error as AxiosError<ApiError>
      showErrorToast(
        'Ошибка',
        err.response?.data.error.details ?? 'Произошла ошибка при получении заказа'
      )
      console.error(error)
    } finally {
      loading.value = false
    }
  }

  return {
    getList,
    getStore,
    createOrder,
    createTableOrder,
    confirmOrderPayment,
    resendOrderPaymentOtp,
    getOrderByUuid,
    getCreatedOrder,
    loading
  }
}
